import {
  GeolocationInvalidatedEvent as GeoInvalidatedEvent,
  GeolocationPassed as GeoPassedEvent,
  GeolocationRejected as GeoRejectedEvent,
  LocationFailure as GeoFailureEvent,
} from '@fanduel/geolocation-sdk';

import { type ProviderType } from '@/features/game-context/types';

export const CLIENT_TO_WRAPPER_EVENT_TYPES = {
  INITIALIZE_HANDSHAKE: 'GC/INITIALIZE_HANDSHAKE',
  SET_PROVIDER_ERROR: 'GC/SET_PROVIDER_ERROR',
  SET_GAME_LOADED: 'GC/SET_GAME_LOADED',
  SET_GAMELOAD_STARTED: 'GC/SET_GAMELOAD_STARTED',
  SET_SPIN_IN_PROGRESS: 'GC/SET_SPIN_IN_PROGRESS',
  SET_AUDIO_ENABLED: 'GC/SET_AUDIO_ENABLED',
  SET_GAME_LOAD_PROGRESS: 'GC/SET_GAME_LOAD_PROGRESS',
  CONTEXT_RECEIVED: 'GC/CONTEXT_RECEIVED',
  CONTEXT_PARAM_MISSING: 'GC/CONTEXT_PARAM_MISSING',
  GEOLOCATION_PROGRESS: 'GC/GEOLOCATION_PROGRESS',
  GEOLOCATION_SUCCESS: 'GC/GEOLOCATION_SUCCESS',
  GEOLOCATION_INVALIDATED: 'GC/GEOLOCATION_INVALIDATED',
  GEOLOCATION_FAILURE: 'GC/GEOLOCATION_FAILURE',
  GEOLOCATION_REJECTED: 'GC/GEOLOCATION_REJECTED',
  FETCH_GAME_STARTED: 'GC/FETCH_GAME_STARTED',
  FETCH_GAME_SUCCESS: 'GC/FETCH_GAME_SUCCESS',
  FETCH_GAME_FAILURE: 'GC/FETCH_GAME_FAILURE',
  UPDATE_GEOCOMPLY_FAILURE: 'GC/UPDATE_GEOCOMPLY_FAILURE',
  UPDATE_GEOCOMPLY_SUCCESS: 'GC/UPDATE_GEOCOMPLY_SUCCESS',
  UPDATE_AUTHENTICATION_FAILURE: 'GC/UPDATE_AUTHENTICATION_FAILURE',
  UPDATE_AUTHENTICATION_SUCCESS: 'GC/UPDATE_AUTHENTICATION_SUCCESS',
  SHOW_FUNDS: 'GC/SHOW_FUNDS',
  RESET_GAME: 'GC/RESET_GAME',
  CLOSE_GAME_LAUNCHER: 'GC/CLOSE_GAME_LAUNCHER',
  RESUME_INCOMPLETE_GAME: 'GC/RESUME_INCOMPLETE_GAME',
  GAME_SWITCH: 'GC/GAME_SWITCH',
  REQUEST_GAME_WINDOW_UI_STATE: 'GC/REQUEST_GAME_WINDOW_UI_STATE',
  DETACH_SESSION_SNAPSHOT: 'GC/DETACH_SESSION_SNAPSHOT',
  LOCATE_USER: 'GC/LOCATE_USER',
} as const;

export type ClientToWrapperType =
  (typeof CLIENT_TO_WRAPPER_EVENT_TYPES)[keyof typeof CLIENT_TO_WRAPPER_EVENT_TYPES];

export type FetchFgpGameError = {
  errorCode: string | unknown;
  message?: string;
};

export type SetErrorPayload = {
  display?: boolean;
  errorCode?: string;
  errorMessage?: string;
  errorParameters?: object;
  errorType?: string;
  errorCategory?: string;
  fullErrorPayload?: unknown;
};

export type GameClientInitialized = {
  type: typeof CLIENT_TO_WRAPPER_EVENT_TYPES.INITIALIZE_HANDSHAKE;
};

export type SetProviderError = {
  type: typeof CLIENT_TO_WRAPPER_EVENT_TYPES.SET_PROVIDER_ERROR;
  payload: SetErrorPayload;
};

export type SetGameLoaded = {
  type: typeof CLIENT_TO_WRAPPER_EVENT_TYPES.SET_GAME_LOADED;
};

export type SetGameLoadStarted = {
  type: typeof CLIENT_TO_WRAPPER_EVENT_TYPES.SET_GAMELOAD_STARTED;
};

export type SetSpinInProgress = {
  type: typeof CLIENT_TO_WRAPPER_EVENT_TYPES.SET_SPIN_IN_PROGRESS;
  payload: boolean;
};

export type SetAudioEnabled = {
  type: typeof CLIENT_TO_WRAPPER_EVENT_TYPES.SET_AUDIO_ENABLED;
  payload: boolean;
};

export type SetGameProgress = {
  type: typeof CLIENT_TO_WRAPPER_EVENT_TYPES.SET_GAME_LOAD_PROGRESS;
  payload: number;
};

export type ContextReceivedMessage = {
  type: typeof CLIENT_TO_WRAPPER_EVENT_TYPES.CONTEXT_RECEIVED;
};

export type ContextParamMissingMessage = {
  type: typeof CLIENT_TO_WRAPPER_EVENT_TYPES.CONTEXT_PARAM_MISSING;
  payload: string;
};

export type GeolocationProgress = {
  type: typeof CLIENT_TO_WRAPPER_EVENT_TYPES.GEOLOCATION_PROGRESS;
};

export type GeolocationSuccess = {
  type: typeof CLIENT_TO_WRAPPER_EVENT_TYPES.GEOLOCATION_SUCCESS;
  payload: GeoPassedEvent;
};

export type GeolocationInvalidated = {
  type: typeof CLIENT_TO_WRAPPER_EVENT_TYPES.GEOLOCATION_INVALIDATED;
  payload: GeoInvalidatedEvent;
};

export type GeolocationFailure = {
  type: typeof CLIENT_TO_WRAPPER_EVENT_TYPES.GEOLOCATION_FAILURE;
  payload: GeoFailureEvent;
};

export type GeolocationRejected = {
  type: typeof CLIENT_TO_WRAPPER_EVENT_TYPES.GEOLOCATION_REJECTED;
  payload: GeoRejectedEvent;
};

export type FetchGameStarted = {
  type: typeof CLIENT_TO_WRAPPER_EVENT_TYPES.FETCH_GAME_STARTED;
};

export type FetchGameSuccessPayload = {
  gamingSessionTrackingId?: string;
};

export type FetchGameSuccess = {
  type: typeof CLIENT_TO_WRAPPER_EVENT_TYPES.FETCH_GAME_SUCCESS;
  payload?: FetchGameSuccessPayload;
};

export type FetchGameFailure = {
  type: typeof CLIENT_TO_WRAPPER_EVENT_TYPES.FETCH_GAME_FAILURE;
  payload?: FetchFgpGameError;
};

export type UpdateGeolocationFailure = {
  type: typeof CLIENT_TO_WRAPPER_EVENT_TYPES.UPDATE_GEOCOMPLY_FAILURE;
};

export type UpdateGeolocationSuccess = {
  type: typeof CLIENT_TO_WRAPPER_EVENT_TYPES.UPDATE_GEOCOMPLY_SUCCESS;
};

export type UpdateAuthenticationFailure = {
  type: typeof CLIENT_TO_WRAPPER_EVENT_TYPES.UPDATE_AUTHENTICATION_FAILURE;
};

export type UpdateAuthenticationSuccess = {
  type: typeof CLIENT_TO_WRAPPER_EVENT_TYPES.UPDATE_AUTHENTICATION_SUCCESS;
};

export type ShowFunds = {
  type: typeof CLIENT_TO_WRAPPER_EVENT_TYPES.SHOW_FUNDS;
};

export type ResetGame = {
  type: typeof CLIENT_TO_WRAPPER_EVENT_TYPES.RESET_GAME;
};

export type CloseGameLauncher = {
  type: typeof CLIENT_TO_WRAPPER_EVENT_TYPES.CLOSE_GAME_LAUNCHER;
};

export type ResumeIncompleteGame = {
  type: typeof CLIENT_TO_WRAPPER_EVENT_TYPES.RESUME_INCOMPLETE_GAME;
  payload: string;
};

export type GameSwitch = {
  type: typeof CLIENT_TO_WRAPPER_EVENT_TYPES.GAME_SWITCH;
  payload: { rgsCode: string; provider: ProviderType };
};

export type RequestGameWindowUIState = {
  type: typeof CLIENT_TO_WRAPPER_EVENT_TYPES.REQUEST_GAME_WINDOW_UI_STATE;
};

export type LocateUser = {
  type: typeof CLIENT_TO_WRAPPER_EVENT_TYPES.LOCATE_USER;
  payload: { domain: 'casino' | 'poker' };
};

export type DetachSessionSnapshot = {
  type: typeof CLIENT_TO_WRAPPER_EVENT_TYPES.DETACH_SESSION_SNAPSHOT;
  payload: {
    fgpGameData: unknown;
    geocomplyToken?: string;
  };
};

export type ProxyMessage =
  | GameClientInitialized
  | ContextReceivedMessage
  | ContextParamMissingMessage
  | GeolocationProgress
  | GeolocationFailure
  | GeolocationRejected
  | GeolocationInvalidated
  | GeolocationSuccess
  | FetchGameStarted
  | FetchGameSuccess
  | FetchGameFailure
  | SetGameLoaded
  | SetGameProgress
  | SetSpinInProgress
  | SetAudioEnabled
  | SetProviderError
  | SetGameLoadStarted
  | ShowFunds
  | ResetGame
  | UpdateGeolocationFailure
  | UpdateGeolocationSuccess
  | UpdateAuthenticationFailure
  | UpdateAuthenticationSuccess
  | CloseGameLauncher
  | ResumeIncompleteGame
  | GameSwitch
  | RequestGameWindowUIState
  | LocateUser
  | DetachSessionSnapshot;
